import axios from '@/axios'

export default {
  // ============================================
  // WORKFLOW TEMPLATES (Platform-level)
  // ============================================

  /**
   * Get available workflow templates for the organization
   * Returns workflows the org has access to with their supported formats
   */
  getAvailableWorkflows () {
    return new Promise((resolve, reject) => {
      axios
        .get('/api/transcode/workflows')
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // SOURCE FILE LINKING
  // ============================================

  /**
   * Link a source file to a segment
   * @param {number} segmentId - Segment ID
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} sourcePath - Path to source file in storage
   * @param {string} sourceFilename - Optional filename override
   */
  linkSourceFile (segmentId, storageConfigId, sourcePath, sourceFilename = null) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/link-file', { segmentId, storageConfigId, sourcePath, sourceFilename })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Link multiple source files to segments (bulk operation)
   * @param {Array} links - Array of { segmentId, storageConfigId, sourcePath, sourceFilename }
   */
  linkSourceFiles (links) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/link-files', { links })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Unlink source file from segment
   * @param {number} segmentId - Segment ID
   */
  unlinkSourceFile (segmentId) {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/api/stitch/unlink/${segmentId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // DURATION VERIFICATION
  // ============================================

  /**
   * Verify segment duration against source file
   * @param {Object} data - { segmentId, sourceDurationSeconds, sourceDurationFrames, sourceCodec, sourceResolution, sourceFrameRate }
   */
  verifyDuration (data) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/verify-duration', data)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Approve a duration mismatch
   * @param {number} segmentId - Segment ID
   * @param {string} approvalNotes - Optional notes explaining approval
   */
  approveMismatch (segmentId, approvalNotes = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/approve-mismatch', { segmentId, approvalNotes })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Reject a duration mismatch (user will need to upload correct file)
   * @param {number} segmentId - Segment ID
   * @param {string} rejectionNotes - Optional notes explaining rejection
   */
  rejectMismatch (segmentId, rejectionNotes = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/reject-mismatch', { segmentId, rejectionNotes })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get bulk verification status for a media/season
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Optional season number (omit for all seasons)
   */
  getVerificationStatus (mediaId, seasonNumber = null) {
    return new Promise((resolve, reject) => {
      const params = { mediaId }
      if (seasonNumber !== null) params.seasonNumber = seasonNumber
      axios
        .get('/api/stitch/verification-status', { params })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Bulk approve all duration mismatches for a season
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Optional season number (omit for all seasons)
   * @param {string} approvalNotes - Notes for audit log
   */
  bulkApproveMismatches (mediaId, seasonNumber = null, approvalNotes = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/bulk-approve', { mediaId, seasonNumber, approvalNotes })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // TIMECODE RECALCULATION
  // ============================================

  /**
   * Manually recalculate timecodes for an episode
   * @param {number} episodeId - Episode ID
   */
  recalculateTimecodes (episodeId) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/recalculate-timecodes', { episodeId })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // STITCH JOB MANAGEMENT
  // ============================================

  /**
   * Submit a stitching job for an episode
   * @param {number} episodeId - Episode ID
   * @param {Object} options - Job options
   * @param {number} options.processingWorkflowId - Processing workflow ID (recommended)
   * @param {number} options.workflowTemplateId - Platform workflow template ID (new system)
   * @param {number} options.outputStorageConfigId - Optional output storage config ID (legacy, use workflow instead)
   * @param {boolean} options.skipStitch - If true, skip stitching (for single complete files that just need encode/QC)
   */
  submitJob (episodeId, options = {}) {
    const { processingWorkflowId = null, workflowTemplateId = null, outputStorageConfigId = null, skipStitch = false } = options
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/submit-job', { episodeId, processingWorkflowId, workflowTemplateId, outputStorageConfigId, skipStitch })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get status of a stitch job
   * @param {string} jobId - Job ID
   */
  getJobStatus (jobId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/stitch/job/${jobId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Cancel a pending stitch job
   * @param {string} jobId - Job ID
   */
  cancelJob (jobId) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/api/stitch/cancel/${jobId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Retry a failed stitch job
   * @param {string} jobId - Job ID
   */
  retryJob (jobId) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/api/stitch/retry/${jobId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // STATUS ENDPOINTS
  // ============================================

  /**
   * Get stitch status for an episode
   * @param {number} episodeId - Episode ID
   */
  getEpisodeStatus (episodeId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/stitch/episode/${episodeId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get stitch status for a season
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Season number
   */
  getSeasonStatus (mediaId, seasonNumber) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/stitch/season/${mediaId}/${seasonNumber}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // STITCH QC MANAGEMENT
  // ============================================

  /**
   * Get stitch QC status for an episode
   * @param {number} episodeId - Episode ID
   */
  getQcStatus (episodeId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/stitch/qc/${episodeId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get presigned URL for QC report PDF
   * @param {number} episodeId - Episode ID
   */
  getQcReport (episodeId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/stitch/qc-report/${episodeId}`)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Override a failed stitch QC
   * @param {number} episodeId - Episode ID
   * @param {string} reason - Reason for override (required, min 10 chars)
   */
  overrideQc (episodeId, reason) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/qc-override', { episodeId, reason })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Reject a stitch QC (requires re-stitching)
   * @param {number} episodeId - Episode ID
   * @param {string} reason - Reason for rejection (optional)
   */
  rejectQc (episodeId, reason = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/stitch/qc-reject', { episodeId, reason })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  // ============================================
  // SEASON-LEVEL OPERATIONS
  // ============================================

  /**
   * Auto-suggest source files for all episodes in a season
   * Scans S3 once and matches files to all episodes using filename patterns
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Season number
   * @param {number} storageConfigId - Storage config ID to scan
   * @param {string} customPattern - Optional learned pattern hint
   * @param {string} path - Optional folder path to search in (from browsing)
   * @returns {Promise<{episodes: Array, stats: Object}>}
   */
  suggestSeasonFiles (mediaId, seasonNumber, storageConfigId, customPattern = null, path = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/suggest-season-files', { mediaId, seasonNumber, storageConfigId, customPattern, path })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Submit stitch jobs for multiple episodes in a season
   * Jobs are queued independently so one failure doesn't stop others
   * @param {number} mediaId - Media ID
   * @param {Array<number>} episodeIds - Array of episode IDs to process (optional, processes all ready episodes if omitted)
   * @param {Object} options - Processing options
   * @param {number} options.processingWorkflowId - Processing workflow ID
   * @param {number} options.outputStorageConfigId - Output storage config ID
   * @returns {Promise<{jobs: Array, stats: Object}>}
   */
  submitSeasonJobs (mediaId, episodeIds = null, options = {}) {
    return new Promise((resolve, reject) => {
      const payload = {
        mediaId,
        ...options
      }
      if (episodeIds && episodeIds.length > 0) {
        payload.episodeIds = episodeIds
      }
      axios
        .post('/api/stitch/submit-season', payload)
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get segment statistics for a media/season
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Optional season number
   * @returns {Promise<{totalSegments: number, linked: number, unlinked: number, verified: number, ...}>}
   */
  getSeasonSegmentStats (mediaId, seasonNumber = null) {
    return new Promise((resolve, reject) => {
      const params = {}
      if (seasonNumber !== null) params.seasonNumber = seasonNumber
      axios
        .get(`/api/segments/stats/${mediaId}`, { params })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Bulk link source files to segments
   * @param {Array} links - Array of { segmentId, filePath, fileSize, actualDurationMs }
   * @returns {Promise<{linked: number, mismatches: Array, errors: Array}>}
   */
  bulkLinkSegments (links) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/segments/bulk-link', { links })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Approve a duration mismatch (updates segment timing)
   * @param {number} segmentId - Segment ID
   * @param {string} notes - Approval notes
   * @returns {Promise<{success: boolean, segment: Object}>}
   */
  approveSegmentMismatch (segmentId, notes = '') {
    return new Promise((resolve, reject) => {
      axios
        .post(`/api/segments/${segmentId}/approve-mismatch`, { notes })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Bulk approve all duration mismatches for segments
   * @param {Array<number>} segmentIds - Array of segment IDs to approve
   * @param {string} notes - Approval notes
   * @returns {Promise<{approved: number, errors: Array}>}
   */
  bulkApproveSegmentMismatches (segmentIds, notes = '') {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/segments/bulk-approve-mismatches', { segmentIds, notes })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Bulk unlink all source files from segments for a media/season
   * Allows re-running auto-suggest after files have been linked
   * @param {number} mediaId - Media ID
   * @param {number} seasonNumber - Optional season number
   * @returns {Promise<{unlinked: number, segmentsUpdated: number, episodeCount: number}>}
   */
  bulkUnlinkSegments (mediaId, seasonNumber = null) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/segments/bulk-unlink', { mediaId, seasonNumber })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  }
}
